import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Drawer from "antd/es/drawer";
import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import { useIntl } from "react-intl";

import FieldVisibility from "@mapmycustomers/shared/enum/FieldVisibility";
import SchemaFieldCategory from "@mapmycustomers/shared/enum/SchemaFieldCategory";
import Role from "@mapmycustomers/shared/types/Role";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import Team from "@mapmycustomers/shared/types/Team";
import { Button } from "@mapmycustomers/ui";

import FieldAccess from "@app/scene/settings/component/CustomFields/component/CreateEditPane/component/FieldAccess";
import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import { isEditedFieldLoading } from "@app/scene/settings/component/ManageFields/store/selectors";
import { getRolesExcludingOwners, getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { createTeamVisibilityFromRoleVisibility } from "@app/util/schema/fieldVisibilityRecord";
import getDefaultDrawerContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import { bulkUpdateField } from "../../store/actions";

import styles from "./BulkEditPane.module.scss";
import messages from "./messages";

interface Props {
  loading: boolean;
  onBulkUpdateField: typeof bulkUpdateField.request;
  open: boolean;
  roles: Role[];
  selectedSchemaFields: SchemaField[];
  setOpen: (open: boolean) => void;
  teams: Team[];
}

const BulkEditPane: React.FC<Props> = ({
  loading,
  onBulkUpdateField,
  open,
  roles,
  selectedSchemaFields,
  setOpen,
  teams,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const [form] = useForm<DefaultCustomFieldFormValues>();
  const bulkUpdateFieldAnalytics = useExtendedAnalytics(["Field", "Bulk Edit Fields"], analytics);

  useEffect(() => {
    bulkUpdateFieldAnalytics?.clicked([]);
  }, [bulkUpdateFieldAnalytics]);

  const handleSave = useCallback(() => {
    bulkUpdateFieldAnalytics?.clicked(["Save"]);
    form.submit();
  }, [bulkUpdateFieldAnalytics, form]);

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const handleBulkEdit = useCallback(() => {
    const formValues = form.getFieldsValue();
    const teamVisibility = formValues.unifiedVisibility
      ? createTeamVisibilityFromRoleVisibility(formValues.roleVisibility, roles, teams)
      : formValues.teamVisibility;

    if (selectedSchemaFields) {
      const updatedSchemaFields: SchemaField[] = selectedSchemaFields
        .filter((schemaField) => {
          const isPrivate = formValues.visibility === FieldVisibility.PRIVATE;
          if (!isPrivate) {
            return true;
          }
          return ![
            SchemaFieldCategory.RELATIONSHIP,
            SchemaFieldCategory.SYSTEM_DEPENDENT,
            SchemaFieldCategory.SYSTEM_REQUIRED,
            SchemaFieldCategory.VARIANT,
          ].includes(schemaField.category as SchemaFieldCategory);
        })
        .map((schemaField) => ({
          ...schemaField,
          phiEnabled: formValues.phiEnabled,
          teamVisibility,
          visibility: formValues.visibility!,
        }));
      if (updatedSchemaFields.length) {
        onBulkUpdateField(updatedSchemaFields);
      }
    }
    handleClose();
  }, [form, handleClose, onBulkUpdateField, roles, selectedSchemaFields, teams]);

  const selectedSchemaFieldsCount = selectedSchemaFields.length;

  useHiddenChat();

  return (
    <Drawer
      className={styles.container}
      closable
      getContainer={getDefaultDrawerContainer()}
      onClose={handleClose}
      open={open}
      placement="right"
      title={intl.formatMessage(messages.title, { count: selectedSchemaFieldsCount })}
      zIndex={1}
    >
      <div className={styles.content}>
        <Form<DefaultCustomFieldFormValues>
          form={form}
          initialValues={{
            phiEnabled: false,
            roleVisibility: {},
            teamVisibility: [],
            unifiedVisibility: false,
            visibility: FieldVisibility.FULL_ACCESS,
          }}
          layout="vertical"
          onFinish={handleBulkEdit}
        >
          <FieldAccess selectedSchemaFields={selectedSchemaFields} />
        </Form>
      </div>

      <div className={styles.footer}>
        <Button loading={loading} onClick={handleSave} type="primary">
          {intl.formatMessage(messages.editButton, { count: selectedSchemaFieldsCount })}
        </Button>
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isEditedFieldLoading(state),
  roles: getRolesExcludingOwners(state),
  teams: getTeams(state),
});

const mapDispatchToProps = {
  onBulkUpdateField: bulkUpdateField.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkEditPane);
